<template>
  <div class="page-view">
    <div class="page-toolbar">
      <el-input
        v-model="searchQuery"
        class="page-search"
        placeholder="搜索昵称 / 用户名 / 微信号"
        clearable
        size="small"
      />
      <el-select
        v-model="onlineFilter"
        class="page-online-filter"
        size="small"
        style="width: 132px;"
      >
        <el-option label="全部" value="all" />
        <el-option :label="`启用 (${enabledCount})`" value="enabled" />
        <el-option :label="`在线 (${onlineCount})`" value="online" />
        <el-option :label="`离线 (${offlineCount})`" value="offline" />
        <el-option :label="`禁用 (${disabledCount})`" value="disabled" />
      </el-select>
      <el-pagination
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="filteredData.length"
        :page-sizes="[20, 50, 100]"
        :layout="paginationLayout"
        :small="isMobile"
        :pager-count="isMobile ? 5 : 7"
      />
    </div>
    <div class="table-wrap" :style="tableWrapStyle">
      <el-auto-resizer>
        <template #default="{ height, width }">
          <el-table-v2 :columns="columns" :data="paginatedData" :width="width" :height="height" fixed />
        </template>
      </el-auto-resizer>
    </div>
  </div>
  <el-dialog
    v-model="dialogVisible"
    title="扫码登录"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    :align-center="true"
  >
    <div style="text-align: center;">
      <div style="margin-bottom: 10px; color: #666; font-size: 14px;">
        当前设备: <span style="color: #409eff; font-weight: bold;">{{ deviceLabel(currentDevice) }}</span>
      </div>
      <el-image style="width: 200px; height: 200px;" :src="qrCodeUrl" fit="fill" />
      <div style="margin-top: 15px;">
        <el-input 
          v-model="verifyCode" 
          placeholder="请输入验证码" 
          style="width: 200px;"
          clearable
        />
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="switchDevice">切换新设备</el-button>
        <el-select
          v-model="proxyArea"
          placeholder="选择代理区域"
          style="width: 120px; margin-left: 8px;"
          size="small"
        >
          <el-option
            v-for="area in ProxyArea"
            :key="area"
            :label="area"
            :value="area"
          />
        </el-select>
        <el-button type="primary" @click="reloginComplete()">已扫</el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
      </span>
    </template>
  </el-dialog>
  <WebVerifyDialog
    v-model="webVerifyVisible"
    :url="followUpUrl"
    @fallback="followUpUrlVisible = true"
    @completed="reloginComplete"
  />
  <UrlDialog v-model="followUpUrlVisible" :url="followUpUrl" />
</template>

<script setup lang="tsx">
import { computed, reactive, ref, watch } from "vue";
import { getWechatAccounts, setWechatAccountDisabled, wechatRelogin, wechatLogin, wechatRefreshFriendsInfo } from "../api/http";
import { ElButton, ElMessage, ElMessageBox, ElAvatar } from "element-plus";
import router from "../router/index";
import { ProxyArea } from "../api/util";
import UrlDialog from "../components/UrlDialog.vue";
import WebVerifyDialog from "../components/WebVerifyDialog.vue";
import {
  Device,
  DEFAULT_DEVICE,
  deviceLabel,
  type DeviceType,
  type WechatAccount,
} from "../api/models";
import { useBreakpoint } from "../composables/useBreakpoint";

const { isMobile } = useBreakpoint();

const dialogVisible = ref(false);
const qrCodeUrl = ref('');
const verifyCode = ref('');
const currentDevice = ref<DeviceType>(DEFAULT_DEVICE);
const proxyArea = ref('安徽');
const followUpUrlVisible = ref(false);
const followUpUrl = ref('');
const webVerifyVisible = ref(false);
const session = reactive({ platformId: "", wId: "", wcId: "" });

const onlineDot = (online: boolean, disabled = false) => (
  <span
    title={disabled ? "禁用" : online ? "在线" : "离线"}
    style={{
      display: "inline-block",
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      background: disabled ? "#f56c6c" : online ? "#67c23a" : "#c0c4cc",
    }}
  ></span>
);

const desktopColumns = [{
  key: "order",
  dataKey: "order",
  title: "#",
  width: 60,
  align: "center",
}, {
  key: "avatar",
  dataKey: "smallHeadImgUrl",
  title: "Avatar",
  width: 70,
  align: "center",
  cellRenderer: ({ cellData: url }: { cellData: string }) => (
    <>
      <ElAvatar shape="square" src={url}></ElAvatar>
    </>
  ),
}, {
  key: "username",
  dataKey: "username",
  title: "Username",
  width: 160,
}, {
  key: "wAccount",
  dataKey: "wAccount",
  title: "Wechat Account",
  width: 160,
}, {
  key: "wcId",
  dataKey: "wcId",
  title: "Wechat Id",
  width: 180,
}, {
  key: "nickName",
  dataKey: "nickName",
  title: "Nick Name",
  width: 180,
  flexGrow: 1,
}, {
  key: "online",
  dataKey: "online",
  title: "状态",
  width: 70,
  align: "center",
  cellRenderer: ({ rowData }: { rowData: WechatAccount }) => onlineDot(rowData.online, rowData.disabled),
}, {
  key: "actions",
  dataKey: "wcId",
  title: "Actions",
  width: 390,
  align: "center",
  fixed: "right" as const,
  cellRenderer: ({ rowData }: { rowData: any }) => (
    <>
      <ElButton size="small" onClick={() => { router.push("/wechat_detail/" + rowData.wcId); }}>账号详情</ElButton>
      <ElButton size="small" disabled={rowData.disabled} onClick={() => { relogin(rowData.wcId, rowData.platformId, rowData.deviceType, rowData.area); }}>重新登录</ElButton>
      <ElButton size="small" disabled={rowData.disabled} onClick={() => { refreshContacts(rowData.wcId); }}>刷新群聊和好友</ElButton>
      <ElButton size="small" type={rowData.disabled ? "success" : "danger"} loading={changingAccounts.has(rowData.wcId)} onClick={() => { changeDisabled(rowData); }}>{rowData.disabled ? "启用" : "禁用"}</ElButton>
    </>
  ),
}];

const mobileColumns = [{
  key: "avatar",
  dataKey: "smallHeadImgUrl",
  title: "",
  width: 50,
  align: "center",
  cellRenderer: ({ cellData: url }: { cellData: string }) => (
    <>
      <ElAvatar shape="square" size={32} src={url}></ElAvatar>
    </>
  ),
}, {
  key: "nickName",
  dataKey: "nickName",
  title: "Nick Name",
  width: 100,
  flexGrow: 1,
}, {
  key: "online",
  dataKey: "online",
  title: "",
  width: 30,
  align: "center",
  cellRenderer: ({ rowData }: { rowData: WechatAccount }) => onlineDot(rowData.online, rowData.disabled),
}, {
  key: "actions",
  dataKey: "wcId",
  title: "Actions",
  width: 250,
  align: "center",
  fixed: "right" as const,
  cellRenderer: ({ rowData }: { rowData: any }) => (
    <>
      <ElButton size="small" onClick={() => { router.push("/wechat_detail/" + rowData.wcId); }}>详情</ElButton>
      <ElButton size="small" disabled={rowData.disabled} onClick={() => { relogin(rowData.wcId, rowData.platformId, rowData.deviceType, rowData.area); }}>重登</ElButton>
      <ElButton size="small" disabled={rowData.disabled} onClick={() => { refreshContacts(rowData.wcId); }}>刷新</ElButton>
      <ElButton size="small" type={rowData.disabled ? "success" : "danger"} loading={changingAccounts.has(rowData.wcId)} onClick={() => { changeDisabled(rowData); }}>{rowData.disabled ? "启用" : "禁用"}</ElButton>
    </>
  ),
}];

const columns = computed(() => isMobile.value ? mobileColumns : desktopColumns);

const data = ref<WechatAccount[]>([]);

const searchQuery = ref("");
const onlineFilter = ref<"all" | "enabled" | "online" | "offline" | "disabled">("enabled");
const enabledCount = computed(() => data.value.filter((a) => !a.disabled).length);
const disabledCount = computed(() => data.value.filter((a) => a.disabled).length);
const onlineCount = computed(() => data.value.filter((a) => !a.disabled && a.online).length);
const offlineCount = computed(() => data.value.filter((a) => !a.disabled && !a.online).length);
const filteredData = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return data.value.filter((a) => {
    if (onlineFilter.value === "disabled" && !a.disabled) return false;
    if (onlineFilter.value !== "all" && onlineFilter.value !== "disabled" && a.disabled) return false;
    if (onlineFilter.value === "online" && !a.online) return false;
    if (onlineFilter.value === "offline" && a.online) return false;
    if (!q) return true;
    return (
      (a.nickName ?? "").toLowerCase().includes(q) ||
      (a.username ?? "").toLowerCase().includes(q) ||
      (a.wAccount ?? "").toLowerCase().includes(q) ||
      (a.wcId ?? "").toLowerCase().includes(q)
    );
  });
});

const pageNum = ref(1);
const pageSize = ref(20);
const paginatedData = computed(() => {
  const start = (pageNum.value - 1) * pageSize.value;
  return filteredData.value.slice(start, start + pageSize.value);
});
const paginationLayout = computed(() => (isMobile.value ? "prev, pager, next" : "total, sizes, prev, pager, next, jumper"));

watch([searchQuery, onlineFilter], () => { pageNum.value = 1; });

const tableWrapStyle = computed(() =>
  isMobile.value
    ? { height: 50 + Math.max(1, paginatedData.value.length) * 50 + "px", flex: "none", minHeight: "0" }
    : undefined
);

const loadWechatAccounts = () => {
  return getWechatAccounts("all").then(res => {
    if (res.successful) {
      res.result.forEach((item, index) => {
        item.order = index + 1;
      });
      data.value = res.result;
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  });
}

const changingAccounts = reactive(new Set<string>());
const changeDisabled = async (account: WechatAccount) => {
  if (changingAccounts.has(account.wcId)) return;
  const disabled = !account.disabled;
  if (disabled) {
    try {
      await ElMessageBox.confirm(
        `禁用「${account.nickName || account.wcId}」后将退出微信登录，并从默认列表隐藏。`,
        "禁用微信账号", { type: "warning", confirmButtonText: "禁用并退出", cancelButtonText: "取消" },
      );
    } catch { return; }
  }
  changingAccounts.add(account.wcId);
  try {
    const res = await setWechatAccountDisabled(account.wcId, disabled);
    if (!res.successful) { ElMessage.error(res.message); return; }
    Object.assign(account, { disabled, online: false });
    pageNum.value = Math.min(pageNum.value, Math.max(1, Math.ceil(filteredData.value.length / pageSize.value)));
    ElMessage.success(disabled ? "已禁用并退出登录" : "已启用，请重新登录微信");
  } catch {
    ElMessage.error(disabled ? "禁用失败，请重试" : "启用失败，请重试");
  } finally { changingAccounts.delete(account.wcId); }
};

const switchDevice = () => {
  currentDevice.value = Device.MAC;
  relogin(session.wcId, session.platformId, currentDevice.value);
}

const relogin = (wcId: string, platformId: string, deviceType: string | undefined, area?: string) => {
  session.wcId = wcId;
  session.platformId = platformId;
  if (area) {
    proxyArea.value = area;
  }
  currentDevice.value = deviceType === Device.MAC ? Device.MAC : Device.IPAD;

  wechatRelogin(wcId, currentDevice.value).then(res => {
    if (res.successful) {
      session.wId = res.result.wId;
      qrCodeUrl.value = res.result.qrCodeUrl;
      dialogVisible.value = true;
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  }).catch(() => {
    ElMessage({
      message: "重新登录失败",
      type: "error",
    });
  });
}

const refreshContacts = (wcId: string) => {
  wechatRefreshFriendsInfo(wcId).then(res => {
    if (res.successful) {
      const r = res.result as { message?: string } | undefined;
      ElMessage({ message: r?.message ?? "已开始后台刷新", type: "info" });
    } else {
      ElMessage({ message: res.message, type: "error" });
    }
  }).catch(() => {
    ElMessage({ message: "刷新请求发送失败", type: "error" });
  });
}

const reloginComplete = () => {
  wechatLogin(session.platformId, session.wId, verifyCode.value, proxyArea.value, currentDevice.value).then(res => {
    if (res.successful) {
      if (typeof res.result === "string") {
        // Risk control wants the graphical (slider) verification. Try the
        // automatic web-verify (server-side faked-WeChat browser; operator just
        // drags the slider); @fallback drops back to the manual QR (UrlDialog),
        // @completed re-runs reloginComplete to finish login.
        followUpUrl.value = res.result;
        webVerifyVisible.value = true;
      } else if (res.result.wcId) {
        dialogVisible.value = false;
        ElMessage({
          message: "登录成功",
          type: "success",
        });
        // Refresh the table so the freshly-online account no longer
        // shows the stale "offline" status (SPEC M1).
        loadWechatAccounts();
      }
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  }).catch(() => {
    ElMessage({
      message: "登录失败",
      type: "error",
    });
  });
}

loadWechatAccounts();
</script>
